import {isEmpty} from "es-toolkit/compat";

import {generateInitialState} from "@/helpers/generateInitialState";
import {createSlice, isAnyOf} from "@reduxjs/toolkit";
import {ordersInitialState} from "../initialStates";
import {getOrderById, loadLastOrderDate, loadOrders, loadPreBookOrders} from "./ordersActions";

const initialState = await generateInitialState("orders", ordersInitialState);

const ordersSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    updateCustomerOrders: (state, action) => {
      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
      state.data = action.payload;
    },
    setModalOrderId: (state, action) => {
      state.modalOrderId = action.payload;
      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
    },
    clearModalOrderId: (state, action) => {
      state.modalOrderId = null;
      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
    },
    updateOrderMedicalInfo: (state, action) => {
      const {orderId, orderMedicalInfo} = action.payload;

      const newOrders = state.data.map(order => {
        if (order.orderId === orderId) {
          return {
            ...order,
            medicalHistory: {
              ...order.medicalHistory,
              ...orderMedicalInfo,
            },
          };
        }
        return order;
      });

      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
      state.data = newOrders;
    },
    updateOrders: (state, action) => {
      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
      state.data = [...action.payload];
    },
    setRequestType: (state, action) => {
      state.requestType = action.payload;
      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
    },
    updatePreBookOrders: (state, action) => {
      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
      state.preBook = !isEmpty(action.payload) ? [...(action?.payload || [])] : [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadOrders.fulfilled, (state, action) => {
        state.data = [...(action?.payload || [])];
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
      })
      .addCase(loadLastOrderDate.fulfilled, (state, action) => {
        state.lastOrder = {...action.payload};
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
      })
      .addCase(loadPreBookOrders.fulfilled, (state, action) => {
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
        state.preBook = !isEmpty(action.payload) ? [...(action?.payload || [])] : [];
      })
      .addCase(getOrderById.fulfilled, (state, action) => {
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
        state.data = [action.payload, ...state.data];
      })
      .addMatcher(
        isAnyOf(loadOrders.pending, loadLastOrderDate.pending, loadPreBookOrders.pending, getOrderById.pending),
        (state, action) => {
          state.isFetching = true;
          state.hasFailed = false;
          state.errorMsg = "";
        },
      )
      .addMatcher(
        isAnyOf(loadOrders.rejected, loadLastOrderDate.rejected, loadPreBookOrders.rejected, getOrderById.rejected),
        (state, action) => {
          const {message = "Something went wrong"} = action.payload;

          state.isFetching = false;
          state.hasFailed = true;
          state.errorMsg = message;
        },
      );
  },
  selectors: {
    ordersSelector: state => state.data,
    modalOrderIdSelector: state => state.modalOrderId,
    requestTypeSelector: state => state.requestType,
    lastOrderSelector: state => state.lastOrder,
    preBookOrdersSelector: state => state.preBook,
    ordersStatusSelector: state => ({
      isFetching: state.isFetching,
      hasFailed: state.hasFailed,
      errorMsg: state.errorMsg,
    }),
  },
});

export const {
  clearModalOrderId,
  setModalOrderId,
  setRequestType,
  updateCustomerOrders,
  updateOrderMedicalInfo,
  updateOrders,
  updatePreBookOrders,
} = ordersSlice.actions;

export const {
  lastOrderSelector,
  modalOrderIdSelector,
  ordersSelector,
  ordersStatusSelector,
  preBookOrdersSelector,
  requestTypeSelector,
} = ordersSlice.selectors;

export default ordersSlice.reducer;
